import { type Node, variablesOf } from "@/lib/tool-kit/calculus/ast";
import { toText } from "@/lib/tool-kit/calculus/print";
import type { PointValue } from "@/lib/tool-kit/calculus/value";

export type Format = (
  value: number,
  options?: Intl.NumberFormatOptions
) => string;

/** A decimal with up to 8 significant digits, in the reader's number format. */
export function decimalText(fmt: Format, value: number): string {
  if (!Number.isFinite(value)) {
    return "—";
  }
  if (value === 0) {
    return fmt(0);
  }
  const magnitude = Math.abs(value);
  if (magnitude >= 1e9 || magnitude < 1e-6) {
    return fmt(value, { notation: "scientific", maximumFractionDigits: 6 });
  }
  return fmt(value, { maximumSignificantDigits: 8 });
}

/** An expression for display: Unicode, decimals if the user typed decimals. */
export function shown(node: Node, decimals: boolean): string {
  return toText(node, { style: "unicode", decimals });
}

/**
 * "7/3 ≈ 2.3333333", or just "5" for an integer, or the decimal alone when no closed
 * form exists. `undefinedText` is shown where the expression has no real value.
 */
export function pointValueText(
  point: PointValue,
  fmt: Format,
  undefinedText: string,
  decimals = false
): string {
  if (!Number.isFinite(point.value)) {
    return point.exact === null && variablesOf(point.symbolic).length > 0
      ? shown(point.symbolic, decimals)
      : undefinedText;
  }
  const decimal = decimalText(fmt, point.value);
  if (point.exact === null) {
    return decimal;
  }
  if (
    point.exact.kind === "num" &&
    point.exact.d === 1 &&
    Number.isInteger(point.exact.n)
  ) {
    return fmt(point.exact.n);
  }
  const exact = shown(point.exact, decimals);
  return exact === decimal ? decimal : `${exact} ≈ ${decimal}`;
}
